import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatCurrency,
  formatEventType,
  formatMiles,
  formatPaymentMethod,
  formatRate,
  getEventTypeBadgeVariant,
  getEventTypeIcon,
  getMiles,
  getOrderType,
  getOrderTypeBadgeVariant,
  getOrderTypeIcon,
  getPaymentMethod,
  getProduct,
  getProductName,
  getRate,
  getTotalPayout,
} from "@/components/utils/events-dialog-utils";
import { Customer } from "@/core";
import { type Event } from "@/core/entities/event";
import { type Product } from "@/core/entities/product";
import { useGetEventsByCustomerId } from "@/hooks";
import { useProductsStore } from "@/stores/products";
import { formatDistanceToNow } from "date-fns";
import {
  Calendar,
  CreditCard,
  DollarSign,
  Eye,
  Loader2,
  RefreshCw,
  Settings2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ProductDetailsDialog } from "./EventDetailsDialog";

interface EventsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
}

// Define available columns
const COLUMNS = {
  type: { key: "type", label: "Type" },
  product: { key: "product", label: "Product" },
  orderType: { key: "orderType", label: "Order Type" },
  miles: { key: "miles", label: "Miles" },
  rate: { key: "rate", label: "Rate" },
  totalPayout: { key: "totalPayout", label: "Total Payout" },
  paymentMethod: { key: "paymentMethod", label: "Payment" },
  created: { key: "created", label: "Created" },
  actions: { key: "actions", label: "Actions" },
} as const;

export const EventsDialog = ({
  open,
  onOpenChange,
  customer,
}: EventsDialogProps) => {
  const eventsData = useGetEventsByCustomerId(
    {
      pagination: {
        limit: 20,
      },
    },
    customer.id,
  );

  const { products } = useProductsStore();

  const handleRefresh = () => {
    eventsData.refetch();
  };

  const events = useMemo(() => {
    if (eventsData.isLoading || !eventsData.data) {
      return [];
    }
    return eventsData.data.pages
      .flatMap((page) => page)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [eventsData.data, eventsData.isLoading]);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  // Column visibility state - all columns visible by default
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      type: true,
      product: true,
      orderType: true,
      miles: true,
      rate: true,
      totalPayout: true,
      paymentMethod: false,
      created: true,
      actions: true,
    },
  );

  const toggleColumn = (columnKey: string) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleViewProduct = (productId: string, event: Event) => {
    const product = getProduct(productId, products);
    if (product) {
      setSelectedProduct(product);
      setSelectedEvent(event);
      setProductDialogOpen(true);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-4xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>Customer Events - {customer.firstName}</SheetTitle>
          <SheetDescription>
            View all events and activities for this customer. This includes form
            submissions, quotes, and transaction requests.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {eventsData.isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Events</h3>
              <p className="text-muted-foreground">
                Fetching customer events...
              </p>
            </div>
          )}
          {!eventsData.isLoading && events.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Events Found</h3>
              <p className="text-muted-foreground">
                This customer hasn't created any events yet.
              </p>
            </div>
          )}
          {!eventsData.isLoading && events.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Events ({events.length})
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={eventsData.isLoading || eventsData.isFetching}
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${eventsData.isFetching ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings2 className="h-4 w-4 mr-2" />
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.values(COLUMNS).map((column) => (
                        <DropdownMenuCheckboxItem
                          key={column.key}
                          className="capitalize"
                          checked={visibleColumns[column.key]}
                          onCheckedChange={() => toggleColumn(column.key)}
                        >
                          {column.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.type && <TableHead>Type</TableHead>}
                      {visibleColumns.product && <TableHead>Product</TableHead>}
                      {visibleColumns.orderType && (
                        <TableHead>Order Type</TableHead>
                      )}
                      {visibleColumns.miles && <TableHead>Miles</TableHead>}
                      {visibleColumns.rate && <TableHead>Rate</TableHead>}
                      {visibleColumns.totalPayout && (
                        <TableHead>Total Payout</TableHead>
                      )}
                      {visibleColumns.paymentMethod && (
                        <TableHead>Payment</TableHead>
                      )}
                      {visibleColumns.created && <TableHead>Created</TableHead>}
                      {visibleColumns.actions && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => {
                      const orderType = getOrderType(event);
                      const miles = getMiles(event);
                      const rate = getRate(event);
                      const totalPayout = getTotalPayout(event);
                      const paymentMethod = getPaymentMethod(event);

                      return (
                        <TableRow key={event.id}>
                          {visibleColumns.type && (
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getEventTypeIcon(event.type)}
                                <Badge
                                  variant={getEventTypeBadgeVariant(event.type)}
                                >
                                  {formatEventType(event.type)}
                                </Badge>
                              </div>
                            </TableCell>
                          )}
                          {visibleColumns.product && (
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">
                                  {getProductName(event.productId, products)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {getProduct(event.productId, products)
                                    ?.category ?? "Unknown"}
                                </span>
                              </div>
                            </TableCell>
                          )}
                          {visibleColumns.orderType && (
                            <TableCell>
                              {orderType ? (
                                <div className="flex items-center gap-2">
                                  {getOrderTypeIcon(orderType)}
                                  <Badge
                                    variant={getOrderTypeBadgeVariant(
                                      orderType,
                                    )}
                                  >
                                    {orderType.toUpperCase()}
                                  </Badge>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.miles && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className="font-medium">
                                  {formatMiles(miles)}
                                </span>
                                {miles && (
                                  <span className="text-xs text-muted-foreground">
                                    miles
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          )}
                          {visibleColumns.rate && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3 text-muted-foreground" />
                                <span className="font-medium">
                                  {formatRate(rate)}
                                </span>
                                {rate && (
                                  <span className="text-xs text-muted-foreground">
                                    /mile
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          )}
                          {visibleColumns.totalPayout && (
                            <TableCell>
                              <span className="font-medium text-green-600">
                                {formatCurrency(totalPayout)}
                              </span>
                            </TableCell>
                          )}
                          {visibleColumns.paymentMethod && (
                            <TableCell>
                              {paymentMethod ? (
                                <div className="flex items-center gap-1">
                                  <CreditCard className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-sm">
                                    {formatPaymentMethod(paymentMethod)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.created && (
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-sm">
                                  {formatDistanceToNow(event.createdAt, {
                                    addSuffix: true,
                                  })}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {event.createdAt.toLocaleDateString()}
                                </span>
                              </div>
                            </TableCell>
                          )}
                          {visibleColumns.actions && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleViewProduct(event.productId, event)
                                }
                                className="h-8 w-8 p-0"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>

      {selectedProduct && (
        <ProductDetailsDialog
          open={productDialogOpen}
          onOpenChange={setProductDialogOpen}
          product={selectedProduct}
          event={selectedEvent}
        />
      )}
    </Sheet>
  );
};
